<script lang="ts">
export default {
  name: "MainFobizzNavbarTabs",
};
</script>

<script setup lang="ts">
import { type RouteRecordName } from "vue-router";
import logoUrl from "/images/fobizz-logo-F.png";

const { t } = useI18n();
const route = useRoute();
const userStore = useUserStore();

const getCurrentTabName = (routeName?: RouteRecordName | null) => {
  switch (routeName) {
    case "tools":
      return t("mainFobizzNavbar.navbarTabs.tools");
    case "gallery-index":
      return t("mainFobizzNavbar.navbarTabs.material");
    case "gallery-community":
      return t("mainFobizzNavbar.navbarTabs.material");
    case "/collections/[id]/":
      return t("mainFobizzNavbar.navbarTabs.material");
    case "collections-how-to":
      return t("mainFobizzNavbar.navbarTabs.material");
    case "partners-index":
      return t("mainFobizzNavbar.navbarTabs.partners");
    case "international-partners-show":
      return t("mainFobizzNavbar.navbarTabs.partners");
    default:
      return "";
  }
};

const showPartnersTab = computed(() => {
  return !(
    userStore.platformUser &&
    (!userStore.platformUser.partnersEnabled ||
      userStore.isDisabledToolsFeature("partners"))
  );
});

const homeRouteActive = computed(() => {
  if (userStore.isFeatureEnabled("startpage_redesign")) {
    return ["tools", "bookmark-index"].includes(route.name as string);
  }
  return ["home", "tools", "bookmark-index"].includes(route.name as string);
});

const galleryRouteActive = computed(() => {
  return ["gallery-index", "gallery-community"].includes(route.name as string);
});

const partnerRouteActive = computed(() => {
  return ["partners-index", "international-partners-show"].includes(
    route.name as string
  );
});
</script>

<template>
  <!-- huge desktop navigation -->
  <nav aria-label="Main">
    <ul class="navbar-nav main-fobizz-navbar__tabs">
      <li class="nav-item d-none d-xl-flex">
        <a
          href="/fortbildungen"
          class="main-fobizz-navbar__link main-fobizz-navbar__tab-link"
        >
          {{ t("mainFobizzNavbar.navbarTabs.courses") }}
        </a>
        <router-link
          :to="{ name: 'tools' }"
          class="main-fobizz-navbar__link main-fobizz-navbar__tab-link"
          :class="{
            active: homeRouteActive,
          }"
        >
          {{ t("mainFobizzNavbar.navbarTabs.tools") }}
        </router-link>
        <router-link
          :to="{ name: 'gallery-index' }"
          class="main-fobizz-navbar__link main-fobizz-navbar__tab-link"
          :class="{
            active: galleryRouteActive,
          }"
        >
          {{ t("mainFobizzNavbar.navbarTabs.material") }}
        </router-link>
        <router-link
          v-if="showPartnersTab"
          :to="
            userStore.user?.international
              ? { name: 'international-partners-index' }
              : { name: 'partners-index' }
          "
          class="main-fobizz-navbar__link main-fobizz-navbar__tab-link"
          :class="{
            active: partnerRouteActive,
          }"
          data-click-tracking="true"
        >
          {{ t("mainFobizzNavbar.navbarTabs.partners") }}
        </router-link>
      </li>
    </ul>
  </nav>

  <!-- mobile & desktop navigation -->
  <ul class="navbar-nav d-block d-xl-none main-fobizz-navbar__tabs">
    <li class="nav-item">
      <button
        class="main-fobizz-navbar__link main-fobizz-navbar__link--sm main-fobizz-navbar__tab-link"
        data-bs-toggle="offcanvas"
        data-bs-target="#navbar-tab-offcanvas"
        aria-controls="navbar-tab-offcanvas"
        aria-label="Open navigation"
      >
        <span>{{ getCurrentTabName(route.name) }}</span>
        <BaseIcon icon-name="chevron-down" class="ms-2" size="inherit" />
      </button>

      <div
        id="navbar-tab-offcanvas"
        class="offcanvas offcanvas-top navbar-tabs-offcanvas"
        tabindex="-1"
        aria-labelledby="navbar-tab-offcanvasLabel"
        data-bs-scroll="true"
      >
        <div class="offcanvas-header">
          <p id="navbar-tab-offcanvasLabel" class="offcanvas-title">
            <img :src="logoUrl" width="40" alt="fobizz logo" />
          </p>
          <button
            type="button"
            class="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close navigation"
          ></button>
        </div>

        <div class="offcanvas-body small">
          <nav>
            <a
              href="/fortbildungen"
              class="main-fobizz-navbar__link navbar-tabs-offcanvas__link"
            >
              {{ t("mainFobizzNavbar.navbarTabs.courses") }}
            </a>
            <router-link
              :to="{ name: 'tools' }"
              class="main-fobizz-navbar__link navbar-tabs-offcanvas__link"
              :class="{
                active: homeRouteActive,
              }"
            >
              {{ t("mainFobizzNavbar.navbarTabs.tools") }}
            </router-link>
            <router-link
              :to="{ name: 'gallery-index' }"
              class="main-fobizz-navbar__link navbar-tabs-offcanvas__link"
              :class="{
                active: galleryRouteActive,
              }"
            >
              {{ t("mainFobizzNavbar.navbarTabs.material") }}
            </router-link>
            <router-link
              v-if="showPartnersTab"
              :to="
                userStore.user?.international
                  ? { name: 'international-partners-index' }
                  : { name: 'partners-index' }
              "
              class="main-fobizz-navbar__link navbar-tabs-offcanvas__link"
              :class="{
                active: partnerRouteActive,
              }"
              data-click-tracking="true"
            >
              {{ t("mainFobizzNavbar.navbarTabs.partners") }}
            </router-link>
          </nav>
        </div>
      </div>
    </li>
  </ul>
</template>

<style scoped lang="scss">
.main-fobizz-navbar__tabs {
  flex-direction: row;
  align-items: center;
  width: fit-content;

  .nav-item {
    display: flex;
    align-items: center;
  }

  .login-dropdown {
    &__link {
      &.main-fobizz-navbar__link::before {
        margin: 0 $spacer-3;

        @include media-breakpoint-down(md) {
          margin: 0 $spacer-2;
        }
      }
    }
  }

  .navbar-tabs-offcanvas {
    border-radius: 0 0 $border-radius-lg $border-radius-lg;

    &__link {
      width: fit-content;
      font-size: $h5-font-size !important;

      &.active {
        font-weight: $font-weight-semibold;
      }

      &.main-fobizz-navbar__link::before {
        @include media-breakpoint-down(md) {
          left: $spacer-2;
          right: $spacer-2;
        }
      }
    }
  }
}

.main-fobizz-navbar__tab-link {
  border: none;
  background-color: transparent;

  @include media-breakpoint-down(md) {
    padding: 0 $spacer-2;
  }

  &.active {
    font-weight: $font-weight-semibold;
  }

  &::before {
    @include media-breakpoint-down(lg) {
      left: $spacer-2;
      right: $spacer-2;
    }
  }
}
</style>
